import {
    PlaneCanvas,
    Plane,
    FireCell,
    isFireCellProps,
    SceneCreator,
    type SceneTerrain,
    WindDirections,
    calcWindChange,
    getFireState,
    FireDanger,
    WindSpeed,
    isButtonElement,
    enableAdminMode,
    CellProps,
    addCellInspectorToggle,
    getBoolFromParams,
    setupCounter,
    launchTerrainTweakerModal,
    getConstants,
    setupControls as simkitSetupControls,
    setupInstructionDialog,
    supportsCSSTransformsOnSVG,
    Controls,
    setupDrawButton,
    injectTerrainTweakerStyles,
    WindConvention
} from "@firesim/simkit-core"
// Import ui-components wind slider
import { WindSliderComponent } from "@firesim/ui-components";
// Import smoke simulation
import {setupSmoke} from './setup-smoke';
import { bindLoadButton } from "./loadRuralBlockData";
import { hideHouseBurnedWarning, enableWarning } from "./house-burned-warning";

import { setupPlaceIgnitionButton } from "./draw-ignition";
import type { Config } from "./config";
import { TerrainCodes, terrain, terrainCodeValues, terrainTypes } from "./terrain-types";

// These polyfills drastically increase the build size
// They should be reviewed when IE11 support is no longer required
import "@babel/polyfill";
import "whatwg-fetch";


/*
const drawImageShape = async (plane: Plane) => {
    const data = await createImageMask('images/grass.png', 100, 100);
    console.log('Image', data);

    data.forEach((row, y) => {
        row.forEach((isMasked, x) => {
            if(isMasked) {
                plane.drawCell(x, y, 255, 255, 255, 250);
            }
        });
    });
};
*/

export function setupBlock(config: Config) {
  document.addEventListener("DOMContentLoaded", async () => {
    if (!supportsCSSTransformsOnSVG()) {
      const controls = document.querySelector(".controls");
      if (controls) {
        controls.classList.remove("svg-supported");
      }
    }

    //browserUpdate({required: {e: 44}});


    // Setup plane
    const firecellFactory = (props: CellProps<TerrainCodes, FireCell<TerrainCodes>>) => {
        if(isFireCellProps(props)) {
            return new FireCell(props);
        }
        return new FireCell({
            fuelLoad: 0,
            ...props
        });
    }

    const sceneCreator = new SceneCreator<FireCell<TerrainCodes>, TerrainCodes>(
      firecellFactory,
    );

    const cellData = await sceneCreator.loadTerrainFromJson(config.planeData, terrainCodeValues);
    const cells = sceneCreator.loadCellsFromData(cellData, terrainTypes);


    const canvas = document.getElementById("canvas") as HTMLCanvasElement;

    const layers = [
      "background",
      "sprites",
      "main",
      "buildings",
      "fire",
      "smoke",
      "effects",
    ];

    const canvasDrawer = new PlaneCanvas(canvas, { layers, cellScale: config.scale || 1 });
    const plane = new Plane<TerrainCodes, FireCell<TerrainCodes>>(
      cells,
      canvasDrawer,
    );

    await sceneCreator.loadBackground({
        imgSrc: config.backgroundImage, 
        backgroundColor: config.backgroundColor,
        plane
    });

    plane.agentRunOrder = ["FrontAgent", "EmberAgent"];

    const controls = new Controls(plane);

    const state = getFireState(plane);

    state.setWindDirection(WindDirections.N);
    state.setWindConvention(WindConvention.Toward);

    plane.addTickPrerunner(() => {
      let currentWindSpeed = state.currentWindSpeed;
      let windSpeed = state.windSpeed;
      state.setCurrentWindSpeed(calcWindChange(currentWindSpeed, windSpeed));
      //state.setThrowDistance(calcThrowDistance(currentWindSpeed));
      state.setThrowDistance(0);
    });

    const sceneTerrain: SceneTerrain<TerrainCodes> = {
      buildings: terrainTypes[TerrainCodes.House],
      roads: terrainTypes[TerrainCodes.Road],
      trees: terrainTypes[TerrainCodes.Trees],
      default: terrainTypes[TerrainCodes.Grassland],
    };

    controls.fireDanger = FireDanger.Medium;
    controls.windSpeed = WindSpeed.Light;
    controls.windDirection = WindDirections.N;

    // Setup smoke simulation
    // Initialize smoke simulation with autonomous rendering to 'effects' layer
    setupSmoke(plane, state);

    // Setup all controls using legacy simkit system
    simkitSetupControls(plane);

    // Setup wind slider component
    const windSlider = new WindSliderComponent({
        sliderId: 'windSpeedSlider',
        imageContainerId: 'windSliderImage',
        defaultValue: 2 // Start with Light wind
    });
    
    // Add change event listener
    windSlider.addEventListener('change', (event: Event) => {
        const customEvent = event as CustomEvent;
        const value = customEvent.detail.value;
        // Update the hidden select element and let simkit Controls handle the state
        const hiddenSelect = document.getElementById('windSpeedInput') as HTMLSelectElement;
        if (hiddenSelect) {
            hiddenSelect.value = value.toString(); // Direct mapping: slider 1-4 = WindSpeed 1-4
            hiddenSelect.dispatchEvent(new Event('change'));
            console.log('Wind speed slider changed to level:', value);
        }
    });

    //setupIntroDialog();
    setupInstructionDialog();
    //plane.addEventListener("click", showInstructions);

    enableAdminMode(plane);

    setupDrawButton({
        terrain: terrainTypes[TerrainCodes.Break],
        selector: '#drawSmallBreakBtn',
        radius: 2,
        coverage: 0.9,
        delayTime: 1,
        plane,
        excludeTerrain: [TerrainCodes.House, TerrainCodes.Road, TerrainCodes.Water, TerrainCodes.GrassyDirt, TerrainCodes.Trees, TerrainCodes.PukimaniPoles, TerrainCodes.VineForest]
    });
    setupDrawButton({
        terrain: terrainTypes[TerrainCodes.Break],
        selector: '#drawBigBreakBtn',
        radius: 6,
        coverage: 0.9,
        delayTime: 0,
        plane,
        excludeTerrain: [TerrainCodes.House, TerrainCodes.Road, TerrainCodes.Water, TerrainCodes.PukimaniPoles]
    });
    setupDrawButton({
        terrain: terrainTypes[TerrainCodes.Grassland],
        selector: '#drawGrassBtn',
        radius: 6,
        coverage: 0.2,
        plane,
        excludeTerrain: [TerrainCodes.House, TerrainCodes.Water, TerrainCodes.Grassland, TerrainCodes.VineForest, TerrainCodes.Trees]
    });

    const ignitionBtn = document.getElementById("ignitionBtn");
    if (!isButtonElement(ignitionBtn)) {
      throw new Error("ignitionBtn is not a button element");
    }
    setupPlaceIgnitionButton(ignitionBtn, plane);

        /*
    setupSpeedControls({
        plane,
        selector: '#speedInput',
        baseSpeed: 500
    })
        */
    bindLoadButton(
      document.getElementById("reloadBtn") as HTMLButtonElement,
      plane,
      () => {
        hideHouseBurnedWarning();
        enableWarning();
      },
    );

    const restartBtn = document.getElementById("restartBtn");
    if (restartBtn) {
      restartBtn.addEventListener("click", (evt) => {
        evt.preventDefault();
        window.location.reload();
      });
    }

    if(getBoolFromParams('inspector')) {
        addCellInspectorToggle(plane);
    }
    if(getBoolFromParams('admin')) {
       injectTerrainTweakerStyles();
       launchTerrainTweakerModal(
            terrainTypes,
            TerrainCodes,
            getConstants()
                
       );
    }

    setupCounter({
        textElement: document.getElementById('tickCounter') as HTMLElement,
        plane,
        mode: "minutes",
        tickQuota: 10
    });

  });

}
